import React, { FC, JSX, PropsWithChildren, useEffect, useRef } from "react";
import { useMatch, useNavigate } from "react-router";
import { Spinner } from "@lookiero/aurora";
import { QueryStatus } from "@lookiero/messaging-react";
import { useViewIsLookInEnabled } from "../../projection/lookIn/react/useViewIsLookInEnabled";
import { useViewIsCategoriesOfferingEnabled } from "../../projection/offering/react/useViewIsCategoriesOfferingEnabled";
import { useViewIsCompleteYourLookOfferingEnabled } from "../../projection/offering/react/useViewIsCompleteYourLookOfferingEnabled";
import { useViewIsSalesOfferingEnabled } from "../../projection/offering/react/useViewIsSalesOfferingEnabled";
import { useViewIsTrendingForYouOfferingEnabled } from "../../projection/offering/react/useViewIsTrendingForYouOfferingEnabled";
import { Routes } from "./routes";
import { useShouldRedirectToCategoriesFromCompleteYourLook } from "./useShouldRedirectToCategoriesFromCompleteYourLook";

interface LookInMiddlewareProps {
  readonly basePath: string;
  readonly loader?: JSX.Element;
  readonly onNotAccessible: () => void;
}

const LookInMiddleware: FC<PropsWithChildren<LookInMiddlewareProps>> = ({
  children,
  basePath,
  loader = <Spinner />,
  onNotAccessible,
}) => {
  const { shouldRedirectToCategoriesFromCYL, isLoading } = useShouldRedirectToCategoriesFromCompleteYourLook();
  const [isLookInEnabled, isLookInEnabledStatus] = useViewIsLookInEnabled();
  const [isCompleteYourLookOfferingEnabled, isCompleteYourLookOfferingEnabledStatus] =
    useViewIsCompleteYourLookOfferingEnabled();
  const [isCategoriesOfferingEnabled, isCategoriesOfferingEnabledStatus] = useViewIsCategoriesOfferingEnabled();
  const [isSalesOfferingEnabled, isSalesOfferingEnabledStatus] = useViewIsSalesOfferingEnabled();
  const [isTrendingForYouOfferingEnabled, isTrendingForYouOfferingEnabledStatus] =
    useViewIsTrendingForYouOfferingEnabled();

  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  const homeRouteMatch = useMatch(`${basePath}${Routes.HOME}`);
  const completeYourLookRouteMatch = useMatch(`${basePath}/${Routes.COMPLETE_YOUR_LOOK}`);
  const categoriesRouteMatch = useMatch(`${basePath}/${Routes.CATEGORIES}`);
  const categoryRouteMatch = useMatch(`${basePath}/${Routes.CATEGORY}`);
  const salesRouteMatch = useMatch(`${basePath}/${Routes.SALES}`);
  const saleRouteMatch = useMatch(`${basePath}/${Routes.SALE}`);
  const trendingForYouRouteMatch = useMatch(`${basePath}/${Routes.TRENDING_FOR_YOU}`);

  const dependenciesLoadedStatuses = [QueryStatus.ERROR, QueryStatus.SUCCESS];
  const dependenciesLoaded =
    !isLoading &&
    isLookInEnabled !== undefined &&
    dependenciesLoadedStatuses.includes(isLookInEnabledStatus) &&
    dependenciesLoadedStatuses.includes(isCompleteYourLookOfferingEnabledStatus) &&
    dependenciesLoadedStatuses.includes(isCategoriesOfferingEnabledStatus) &&
    dependenciesLoadedStatuses.includes(isSalesOfferingEnabledStatus) &&
    dependenciesLoadedStatuses.includes(isTrendingForYouOfferingEnabledStatus);

  useEffect(() => {
    if (
      (completeYourLookRouteMatch && isCompleteYourLookOfferingEnabled === false) ||
      ((categoriesRouteMatch || categoryRouteMatch) && isCategoriesOfferingEnabled === false) ||
      ((salesRouteMatch || saleRouteMatch) && isSalesOfferingEnabled === false) ||
      (trendingForYouRouteMatch && isTrendingForYouOfferingEnabled === false)
    ) {
      navigateRef.current(`${basePath}/${Routes.HOME}`, { replace: true });
    }

    if (homeRouteMatch && dependenciesLoaded) {
      if (isCompleteYourLookOfferingEnabled && shouldRedirectToCategoriesFromCYL === false) {
        navigateRef.current(`${basePath}/${Routes.COMPLETE_YOUR_LOOK}`, { replace: true });
      }

      if (isCategoriesOfferingEnabled) {
        navigateRef.current(`${basePath}/${Routes.CATEGORIES}`, { replace: true });
      }

      if (isSalesOfferingEnabled) {
        navigateRef.current(`${basePath}/${Routes.SALES}`, { replace: true });
      }

      if (isTrendingForYouOfferingEnabled) {
        navigateRef.current(`${basePath}/${Routes.TRENDING_FOR_YOU}`, { replace: true });
      }
    }
  }, [
    basePath,
    categoriesRouteMatch,
    categoryRouteMatch,
    completeYourLookRouteMatch,
    dependenciesLoaded,
    homeRouteMatch,
    isCategoriesOfferingEnabled,
    isCompleteYourLookOfferingEnabled,
    isSalesOfferingEnabled,
    isTrendingForYouOfferingEnabled,
    saleRouteMatch,
    salesRouteMatch,
    shouldRedirectToCategoriesFromCYL,
    trendingForYouRouteMatch,
  ]);

  if (!dependenciesLoaded) {
    return loader;
  }

  if (!isLookInEnabled.enabled) {
    onNotAccessible();

    return null;
  }

  return children;
};

export { LookInMiddleware };
